/**
 * ================================================================================================
 *  THE RIBBON SAYS WHERE YOU ARE. IT NEVER SAYS THE DATA IS FAKE WHEN IT IS NOT.
 * ================================================================================================
 *
 * Owner instruction, verbatim, 2026-10-18: *"Replace the prototype's demo-data ribbon with an
 * accurate Staging indicator; never label real data as demo data."*
 *
 * ── WHAT IT REPLACES, AND WHY THAT MATTERED ─────────────────────────────────────────────────────
 *
 * The approved prototype carries a ribbon reading *"הצעת עיצוב · נתוני דוגמה בלבד · אין חיבור
 * למקורות מידע חיים"*. That is TRUE of the prototype and FALSE of the product. Carried across into
 * a deployment it would print a lie over real customer accounting data, on every screen, forever —
 * and the people most likely to believe it are the ones who would otherwise have checked a figure.
 *
 * The product's OWN ribbon today is `fixture-ribbon`, which renders only when `fixturesEnabled()`
 * — i.e. `import.meta.env.DEV`. That one is correct and stays correct: in development the data
 * really is invented. It answers a different question from this one, so both exist and neither
 * speaks for the other.
 *
 * ── AND IT NEVER GUESSES ────────────────────────────────────────────────────────────────────────
 *
 * The value comes from the server, which reads `GLPOINT_ENVIRONMENT` from the runtime env that
 * stage J installs. It is NOT derived from `window.location`, and `ABOUT_SYSTEM_HANDOFF` forbids
 * that by name: *"No hostname substring guessing."* An estate that declared nothing renders
 * "environment unidentified", because the one thing an indicator may never be is confidently
 * wrong — and `staging-admin.glpoint.com` containing the letters "staging" is not a fact about a
 * deployment, it is a fact about a string.
 *
 * ── IT NAMES THE PLACE. IT SAYS NOTHING ABOUT THE DATA. ─────────────────────────────────────────
 *
 * An earlier version of this component added "real data in a staging environment", reasoning that
 * "Test environment" alone might be read as "none of this is real". That was the right worry and
 * the wrong fix: it inferred DATA PROVENANCE from DEPLOYMENT CONTEXT, which are two different
 * facts. A test environment may hold seeded, copied or synthetic rows, and being connected to a
 * live database says nothing about where the rows in it came from.
 *
 * Owner correction, verbatim, 2026-10-18: *"Do not claim real data based solely on the environment
 * or database connection. Label demo or synthetic data separately where its provenance is known."*
 *
 * So this ribbon renders the environment name and nothing else, and the ribbon test holds it
 * to that.
 * Provenance gets its own label wherever the provenance is actually known -- which is exactly what
 * the DEV fixture ribbon already does, and why the two must never be merged into one sentence.
 *
 * ── PRODUCTION SHOWS NOTHING ────────────────────────────────────────────────────────────────────
 *
 * A banner on every screen of the real system is noise that trains people to stop reading banners,
 * and the first thing they would then stop reading is the one that says Staging. Production is the
 * expected place to be; the ribbon exists to say when you are somewhere else.
 */

import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';

export interface EnvironmentRibbonProps {
  /**
   * As the server stated it. `undefined` from a server that predates the field, `null` from a
   * deployment that declared none — both mean "nobody told us", and neither is a licence to guess.
   */
  readonly environment?: string | null;
}

/** The environments that get a ribbon, and what each one is called. */
const LABEL: Readonly<Record<string, TextKey>> = {
  staging: 'env.staging',
  pilot: 'env.pilot',
};

export function EnvironmentRibbon({ environment }: EnvironmentRibbonProps): JSX.Element | null {
  const { t } = useLocale();

  // Production is silent. See the header: a ribbon that is always there is a ribbon nobody reads.
  if (environment === 'production') return null;

  const known = environment === undefined || environment === null ? null : LABEL[environment];

  /*
   * UNIDENTIFIED IS SHOWN, NOT HIDDEN. A deployment that cannot say what it is, is a thing worth
   * knowing — and silence here would be indistinguishable from production.
   */
  if (known === undefined || known === null) {
    return (
      <div className="gp-notice env-ribbon env-ribbon--unknown" role="status" data-environment="unknown">
        <span className="env-ribbon__name">{t('env.unknown')}</span>
      </div>
    );
  }

  return (
    <div className="gp-notice env-ribbon" role="status" data-environment={environment}>
      <span className="env-ribbon__name">{t(known)}</span>
    </div>
  );
}
